import { Components } from "appworks/components/components";
import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { PromptContent } from "appworks/components/prompt/prompt-content";
import { Layers } from "appworks/graphics/layers/layers";
import { gameState } from "appworks/model/game-state";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Services } from "appworks/services/services";
import { State } from "appworks/state-machine/states/state";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { BannerComponent } from "slotworks/components/banner/banner-component";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";

export interface ExitFreespinsConfig {
    includeBaseWin: boolean;
    countupAmountWon: boolean;
    resetTreeToRoot: boolean;
}

export class ExitFreespins extends State {

    protected summaryScenes: string[] = ["freespinssummary", "freespins"];

    protected prompt: PromptContent;

    protected config: ExitFreespinsConfig = {
        includeBaseWin: true,
        countupAmountWon: true,
        resetTreeToRoot: true
    };

    constructor(config?: Partial<ExitFreespinsConfig>) {
        super();

        if (config) {
            this.config = { ...this.config, ...config };
        }
    }

    public onEnter() {

        uiFlags.set(UIFlag.IDLE, false);

        this.prompt = null;
        new Sequence([
            () => this.beforePrompt(),
            () => this.setupPrompt(),
            () => this.showPrompt(),
            () => this.afterPrompt()
        ]).then(() => this.complete());
    }

    public complete() {
        this.setWinnings();
        super.complete();
    }

    protected beforePrompt() {
        return Contract.empty();
    }

    protected setupPrompt() {
        return Contract.wrap(() => {
            uiFlags.set(UIFlag.PROMPT, true);

            const gameplay: Gameplay = gameState.getCurrentGame();

            const freeSpinsWin: number = this.getTotalFreespinsWin();

            let message: string;
            let summaryScene: string;

            if (freeSpinsWin > 0) {
                message = Services.translations.getFirstValid(["fs.wsu", "you_won_x"], { VALUE: Services.currency.format(freeSpinsWin) });
                summaryScene = "freespinssummary";
            } else {
                message = Services.translations.getFirstValid(["fs.nws", "congratulations"]);
                summaryScene = "freespinsnowin";
            }

            const freespinsPlayed = gameplay.getTotalFreespinsWon().toString();

            if (!this.prompt) {
                this.prompt = new PromptContent([summaryScene, ...this.summaryScenes], 4000);
            }

            this.prompt.addContinueButton("continue");

            this.prompt.setText("winMessage", message);
            this.prompt.setText("played", freespinsPlayed);

            if (this.config.countupAmountWon) {
                this.prompt.countupText("value", freeSpinsWin, 2000);
            } else {
                this.prompt.setText("value", Services.currency.format(freeSpinsWin));
            }
        });
    }

    protected showPrompt(): Contract<void> {
        return Components.get(PromptComponent).showPrompt(this.prompt);
    }

    protected afterPrompt(): Contract<void> {
        return new Parallel([
            () => this.hideFreespinsComponents(),
            () => this.resetToTriggeringSpin(),
            () => this.resetMatrix()
        ]);
    }

    protected hideFreespinsComponents(): Contract<void> {
        return new Parallel([
            this.resetLayer(Layers.get("Background")),
            this.resetLayer(Layers.get("MatrixBackground")),
            () => Contract.wrap(() => Components.get(BannerComponent)?.setMode("default"))
        ]);
    }

    protected resetLayer(layer: Layers) {
        if (!layer || layer.getCurrentScene().name === "default") {
            return () => Contract.empty();
        } else {
            return () => layer.defaultScene();
        }
    }

    protected resetToTriggeringSpin() {
        return Contract.wrap(() => {
            const gameplay = gameState.getCurrentGame();

            uiFlags.set(UIFlag.PROMPT, false);
            uiFlags.set(UIFlag.FREE_SPINS, false);

            Services.transaction.setBalance(gameState.getCurrentGame().balance);

            if (this.config.resetTreeToRoot) { gameplay.resetToRootRecord(); }

            Services.transaction.setWinnings(gameplay.getTotalWin()).execute();

            Components.get(AbstractMatrixComponent).clearStuckSymbols();
        });
    }

    protected resetMatrix() {
        const gameplay = gameState.getCurrentGame();
        const record = gameplay.getRootRecord() as SpinRecord;

        if (!gameplay.isRecovered) { // if recovery we don't have the info to recover initial reels
            if (record.stops) {
                Components.get(AbstractMatrixComponent).jump(record.stops, record.reelset);
            } else {
                Components.get(AbstractMatrixComponent).jumpToGrid(record.grid);
            }
        }

        return Contract.empty();
    }

    protected setWinnings(): void {
        const totalWin = gameState.getCurrentGame().getTotalWin();
        Services.transaction.setWinnings(totalWin, totalWin, 750).execute();
    }

    protected getTotalFreespinsWin() {
        const gameplay: Gameplay = gameState.getCurrentGame();

        let freeSpinsWin: number = 0;
        if (this.config.includeBaseWin) {
            freeSpinsWin = gameplay.getTotalWin();
        } else {
            freeSpinsWin = gameplay.getTotalChildrenWin(gameplay.getRootRecord());
        }

        return freeSpinsWin;
    }
}
